// 音声ファイル圧縮（Whisper APIの処理速度向上）

export async function compressAudio(audioBlob: Blob): Promise<Blob> {
  try {
    // WebM → MP3変換（ファイルサイズ削減）
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // モノラル化（ステレオ→モノラルで50%削減）
    const monoBuffer = audioContext.createBuffer(
      1, // チャンネル数
      audioBuffer.length,
      audioBuffer.sampleRate
    );

    // 左右チャンネルの平均を取る
    const channelData = monoBuffer.getChannelData(0);
    for (let i = 0; i < audioBuffer.length; i++) {
      const left = audioBuffer.getChannelData(0)[i];
      const right = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1)[i] : left;
      channelData[i] = (left + right) / 2;
    }

    // OfflineAudioContextで再エンコード
    const offlineContext = new OfflineAudioContext(
      1,
      audioBuffer.length,
      16000 // サンプルレート下げる（44.1kHz → 16kHz、音声には十分）
    );

    const source = offlineContext.createBufferSource();
    source.buffer = monoBuffer;
    source.connect(offlineContext.destination);
    source.start();

    const renderedBuffer = await offlineContext.startRendering();

    // WAVに変換（圧縮率高い）
    const wav = audioBufferToWav(renderedBuffer);
    return new Blob([wav], { type: 'audio/wav' });
  } catch (error) {
    console.error('Audio compression failed, using original:', error);
    return audioBlob; // 失敗したら元のBlobを返す
  }
}

function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const length = buffer.length * buffer.numberOfChannels * 2 + 44;
  const arrayBuffer = new ArrayBuffer(length);
  const view = new DataView(arrayBuffer);
  const channels = [];
  let offset = 0;
  let pos = 0;

  // WAVヘッダー書き込み
  function setUint16(data: number) {
    view.setUint16(pos, data, true);
    pos += 2;
  }

  function setUint32(data: number) {
    view.setUint32(pos, data, true);
    pos += 4;
  }

  // RIFF identifier
  setUint32(0x46464952);
  // file length
  setUint32(length - 8);
  // RIFF type
  setUint32(0x45564157);
  // format chunk identifier
  setUint32(0x20746d66);
  // format chunk length
  setUint32(16);
  // sample format (raw)
  setUint16(1);
  // channel count
  setUint16(buffer.numberOfChannels);
  // sample rate
  setUint32(buffer.sampleRate);
  // byte rate (sample rate * block align)
  setUint32(buffer.sampleRate * 2 * buffer.numberOfChannels);
  // block align (channel count * bytes per sample)
  setUint16(buffer.numberOfChannels * 2);
  // bits per sample
  setUint16(16);
  // data chunk identifier
  setUint32(0x61746164);
  // data chunk length
  setUint32(length - pos - 4);

  // PCMデータ書き込み
  for (let i = 0; i < buffer.numberOfChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }

  while (pos < length) {
    for (let i = 0; i < buffer.numberOfChannels; i++) {
      const sample = Math.max(-1, Math.min(1, channels[i][offset]));
      view.setInt16(pos, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      pos += 2;
    }
    offset++;
  }

  return arrayBuffer;
}
